import React, { useContext } from 'react';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { PROMOTION_TYPES, SearchModes } from '@/domainLogic/index.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { hasPromotion } from '@/app/components/search/components/EvolutionCard/utils/getPromotionsUtils.ts';
import { isSplitPaymentsEligible } from '@/app/components/search/components/DeferredPayments/splitPayments.ts';
import { AgeText, SuccessText, PriceBottomContainer } from './styled';
import Taxes from './Taxes';
import type { DealCard } from '../../../../../utils/buildDealCards/dealCardTypes';
import { CardsContext } from '../../../../SearchPageContainer/ListContainer/CardsGrid/CardsContext.ts';

type PriceBottomProps = {
  isOverlay: boolean;
  taxesText: string | null;
  ageText: string;
  dealCard: DealCard;
  showTotalPrice: boolean;
  showDoublePrice: boolean;
};

// Both lines are off the overlay already, so neither needs to know about it.
const getDepositLabel = ({
  showSplitPayments,
  showDepositAmount,
  t,
}: {
  showSplitPayments?: boolean;
  showDepositAmount?: boolean;
  t: TranslationFunction;
}) => {
  if (showSplitPayments) {
    return <SuccessText>{t('vacation.oss.cards.usp.split_payments')}</SuccessText>;
  }
  if (showDepositAmount) {
    return <SuccessText>{t('vacation.oss.cards.usp.book_now_pay_later')}</SuccessText>;
  }
  return null;
};

const PriceBottom = ({
  isOverlay,
  taxesText,
  ageText,
  dealCard,
  showTotalPrice,
  showDoublePrice,
}: PriceBottomProps) => {
  const { searchInfoData, localization } = usePageWrapperData();
  const { t } = useTranslation();
  const { searchParams, travellersCount } = useContext(CardsContext);
  const { isLiveSearch } = searchInfoData;
  const { promotionTypes } = dealCard;
  const isDP = searchInfoData.searchMode === SearchModes.DP;
  const isHO = searchInfoData.searchMode === SearchModes.HO;
  const taxesIncludedDoublePricing = localization?.configValuesApp?.taxesIncludedDoublePricing;
  const isDoublePricing = isDP && taxesIncludedDoublePricing && !isOverlay;
  const hasDepositAmount = hasPromotion(PROMOTION_TYPES.DEPOSIT_AMOUNT, promotionTypes);
  const showAgeText = ageText && !isOverlay && !isLiveSearch;
  const showTaxesText = taxesText;
  const showTaxes = showTaxesText || isDoublePricing;
  const totalPrice = dealCard?.product?.rate?.price?.total?.price;
  const showSplitPayments = !isOverlay && isSplitPaymentsEligible({ localization, totalPrice, isHO });
  // HO gets the same line as DP: the TSX card shows a deposit USP in this case too
  const showDepositAmount = hasDepositAmount && !isOverlay;
  const depositLabel = getDepositLabel({ showSplitPayments, showDepositAmount, t });

  return showAgeText || showTaxes || showDepositAmount || showSplitPayments ? (
    <PriceBottomContainer className="price-bottom-container" $isOverlay={isOverlay} $isDoublePricing={isDoublePricing}>
      {showTaxes && (
        <Taxes
          isOverlay={isOverlay}
          taxesText={taxesText}
          dealCard={dealCard}
          showTotalPrice={showTotalPrice}
          showDoublePrice={showDoublePrice}
          searchParams={searchParams}
          travellersCount={travellersCount}
        />
      )}
      {showAgeText && <AgeText>{ageText}</AgeText>}
      {depositLabel}
    </PriceBottomContainer>
  ) : null;
};

export default PriceBottom;
